import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
	type ColumnDef,
	type SortingState,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import {
	DataTableColumnHeader,
	DataTableSortProvider,
} from '#app/components/data-table/column-header.tsx'
import { DataTablePagination } from '#app/components/data-table/pagination.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeaderCell,
	TableRow,
} from '#app/components/ui/table.tsx'

export type SubmissionRow = {
	id: string
	name: string | null
	createdAt: string | null
	updatedAt: string | null
}

type Props = {
	readonly data: readonly SubmissionRow[]
	readonly onRowClick: (id: string) => void
	readonly onLoad?: (id: string) => void
	readonly loading?: boolean
}

export function SubmissionsDataTable({
	data,
	onRowClick,
	onLoad,
	loading,
}: Props) {
	const [sorting, setSorting] = useState<SortingState>([
		{ id: 'updatedAt', desc: true },
	])
	const [globalFilter, setGlobalFilter] = useState('')

	const columns = useMemo<ColumnDef<SubmissionRow>[]>(
		() => [
			{
				accessorKey: 'name',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Submitter" />
				),
				cell: ({ row }) => (
					<div className="truncate" title={row.original.name ?? undefined}>
						{row.original.name ?? '—'}
					</div>
				),
				size: 240,
			},
			{
				accessorKey: 'id',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="ID" />
				),
				cell: ({ row }) => (
					<div className="truncate font-mono" title={row.original.id}>
						{row.original.id}
					</div>
				),
				size: 360,
			},
			{
				accessorKey: 'createdAt',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Created" />
				),
				cell: ({ row }) => (
					<span className="text-muted-foreground">
						{formatRelativeCell(row.original.createdAt)}
					</span>
				),
				size: 180,
			},
			{
				accessorKey: 'updatedAt',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Updated" />
				),
				cell: ({ row }) => (
					<span className="text-muted-foreground">
						{formatRelativeCell(row.original.updatedAt)}
					</span>
				),
				size: 180,
			},
			{
				id: 'actions',
				header: () => <span>Actions</span>,
				cell: ({ row }) => (
					<div className="flex justify-end">
						<Button
							type="button"
							size="sm"
							variant="outline"
							disabled={Boolean(loading) || !onLoad}
							onClick={(e) => {
								e.stopPropagation()
								onLoad?.(row.original.id)
							}}
							aria-label={`Load submission ${row.original.id}`}
						>
							Load
						</Button>
					</div>
				),
				enableSorting: false,
				size: 100,
			},
		],
		[onLoad, loading],
	)

	const table = useReactTable({
		data: data as SubmissionRow[],
		columns,
		state: { sorting, globalFilter },
		onSortingChange: setSorting,
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		enableSortingRemoval: false,
		initialState: {
			pagination: { pageSize: 20 },
		},
		globalFilterFn: (row, _columnId, filterValue) => {
			if (!filterValue) return true
			const q = String(filterValue).toLowerCase()
			const id = row.original.id?.toLowerCase() ?? ''
			const name = row.original.name?.toLowerCase() ?? ''
			return id.includes(q) || name.includes(q)
		},
	})

	const requestSort = (columnId: string) => {
		setSorting((prev) => {
			const current = prev[0]
			if (current && current.id === columnId) {
				return [{ id: columnId, desc: !current.desc }]
			}
			return [{ id: columnId, desc: true }]
		})
	}

	return (
		<div className="space-y-3 text-xs">
			<div className="flex items-center justify-between gap-3">
				<input
					className="border-input bg-background text-foreground h-8 w-[320px] rounded-md border px-2 text-xs outline-none"
					placeholder="Search by ID or submitter"
					value={globalFilter}
					onChange={(e) => setGlobalFilter(e.target.value)}
				/>
			</div>

			<DataTableSortProvider value={requestSort}>
				<div className="overflow-hidden rounded border">
					<Table className="border-b-0">
						<TableHead>
							<TableRow>
								{table.getFlatHeaders().map((header) => (
									<TableHeaderCell
										key={header.id}
										style={{ width: header.getSize() }}
										className="py-2 text-xs"
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHeaderCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{table.getRowModel().rows.length > 0 ? (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										className="hover:bg-muted/50 cursor-pointer"
										onClick={() => onRowClick(row.original.id)}
									>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id} className="py-2 text-xs">
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										className="text-muted-foreground h-16 text-center text-xs"
										colSpan={columns.length}
									>
										No submissions found
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
				<DataTablePagination table={table} pageSize={20} hideSelectedSummary />
			</DataTableSortProvider>
		</div>
	)
}

function formatRelativeCell(value: string | null) {
	if (!value) return '—'
	try {
		const d = new Date(value)
		const rel = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
		const delta = (Date.now() - d.getTime()) / 1000
		// Use coarse buckets (minutes/hours/days)
		if (Math.abs(delta) < 3600) {
			const mins = Math.round(delta / 60)
			return rel.format(-mins, 'minute')
		}
		if (Math.abs(delta) < 86400) {
			const hrs = Math.round(delta / 3600)
			return rel.format(-hrs, 'hour')
		}
		const days = Math.round(delta / 86400)
		return rel.format(-days, 'day')
	} catch {
		return '—'
	}
}
